/**
 * ExportDropdown Component - Phase 5 Implementation  
 * Export functionality for reports with multiple format options
 * Supports CSV, JSON, and PDF export with configuration options
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { 
  Download, 
  FileSpreadsheet, 
  FileText, 
  File,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ReportType, ExportFormat, ExportConfig } from '../../types/reports';

interface ExportDropdownProps {
  reportType?: ReportType | null;
  data?: any[];
  onExport: (config: ExportConfig) => void;
  isExporting?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
}

interface ExportOption {
  format: ExportFormat;
  label: string;
  description: string;
  icon: React.ElementType;
  badgeText?: string;
  recommended?: boolean;
}

const exportOptions: ExportOption[] = [
  {
    format: 'csv',
    label: 'CSV File',
    description: 'Spreadsheet format for data analysis',
    icon: FileSpreadsheet,
    badgeText: 'Popular',
    recommended: true
  },
  {
    format: 'json',
    label: 'JSON Data',
    description: 'Structured data for developers',
    icon: File,
  },
  {
    format: 'pdf',
    label: 'PDF Report',
    description: 'Formatted report with charts',
    icon: FileText,
    badgeText: 'Visual'
  }
];

const ExportDropdown: React.FC<ExportDropdownProps> = ({
  reportType,
  data = [],
  onExport,
  isExporting = false,
  disabled = false,
  className,
  label
}) => {
  const [lastExportFormat, setLastExportFormat] = useState<ExportFormat | null>(null);

  const handleExport = (format: ExportFormat) => {
    if (!reportType) {
      return;
    }
    
    const config: ExportConfig = {
      format,
      includeCharts: format === 'pdf',
      includeMetadata: true,
      includeFilters: true,
      dateRange: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        end: new Date()
      },
      filename: `${reportType}-${format}-${Date.now()}`
    };

    setLastExportFormat(format);
    onExport(config);
  };

  const getExportIcon = () => {
    if (isExporting) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    return <Download className="h-4 w-4" />;
  };

  const getButtonText = () => {
    if (isExporting) {
      return `Exporting...`;
    }
    if (label) {
      return label;
    }
    if (lastExportFormat) {
      return `Export (${lastExportFormat.toUpperCase()})`;
    }
    return 'Export';
  };

  const getButtonVariant = () => {
    if (isExporting) return 'secondary';
    if (lastExportFormat) return 'default';
    return 'outline';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={getButtonVariant()}
          size="sm"
          disabled={disabled || isExporting || (!!reportType && data.length === 0)}
          className={cn("flex items-center space-x-2", className)}
        >
          {getExportIcon()}
          <span>{getButtonText()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs uppercase tracking-wide">
          Export Options
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {exportOptions.map((option) => {
          const IconComponent = option.icon;
          const isLastUsed = lastExportFormat === option.format;
          
          return (
            <DropdownMenuItem
              key={option.format}
              onClick={() => handleExport(option.format)}
              className="flex items-start space-x-3 py-3 cursor-pointer"
              disabled={isExporting}
            >
              <div className="flex-shrink-0 mt-0.5">
                <IconComponent className="h-4 w-4 text-muted-foreground" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-sm">
                    {option.label}
                  </span>
                  {option.recommended && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                      {option.badgeText}
                    </Badge>
                  )}
                  {isLastUsed && (
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {option.description}
                </p>
              </div>
            </DropdownMenuItem>
          );
        })}
        
        <DropdownMenuSeparator />
        
        <div className="px-2 py-2">
          <div className="text-xs text-muted-foreground">
            {data.length > 0 ? `${data.length.toLocaleString()} records available` : 'Export all reports'}
          </div>
          {isExporting && (
            <div className="text-xs text-muted-foreground mt-1 flex items-center space-x-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Processing export...</span>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportDropdown;